import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import {
  Box,
  Button,
  ButtonSelect,
  Divider,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { ModalInstance } from "@js/components/modal";
import { RhfTextareaField, RhfTextField } from "@js/rhf/fields";
import { mapServerErrors } from "@js/rhf/utils";
import { isErrorWithDataAndStatus } from "@js/types/errors";
import { enumToOptions } from "@js/utils";

import { useCreateRosterMutation, useUpdateRosterMutation } from "../../api";
import type { Roster, RosterSummary } from "../../types";

import styles from "./styles.module.scss";

const VISIBILITY_OPTIONS = enumToOptions(ENUMS.RosterVisibilityLabels);

const rosterFormSchema = z.object({
  title: z.string().min(1, { message: "Required." }).max(255),
  description: z.string().optional(),
  visibility: z.enum([
    ENUMS.RosterVisibility.PRIVATE,
    ENUMS.RosterVisibility.PUBLIC,
  ]),
});

type RosterFormValues = z.infer<typeof rosterFormSchema>;

export type RosterFormModalContentProps = {
  roster?: RosterSummary;
  canChangeVisibility?: boolean;
  onSaved?: (roster: Roster) => void;
};

export const openRosterFormModal = (props: RosterFormModalContentProps = {}) =>
  ModalInstance.open({
    closeButton: true,
    keepOnBackdropClick: true,
    children: <RosterFormModalContent {...props} />,
    className: styles.modal,
  });

export const RosterFormModalContent = ({
  roster,
  canChangeVisibility = true,
  onSaved,
}: RosterFormModalContentProps) => {
  const [createRoster] = useCreateRosterMutation();
  const [updateRoster] = useUpdateRosterMutation();

  const defaultValues: RosterFormValues = useMemo(
    () => ({
      title: roster?.title ?? "",
      description: roster?.description ?? "",
      visibility: roster?.visibility ?? ENUMS.RosterVisibility.PRIVATE,
    }),
    [roster],
  );

  const { handleSubmit, control, setError, formState } =
    useForm<RosterFormValues>({
      mode: "onTouched",
      resolver: zodResolver(rosterFormSchema),
      defaultValues,
    });

  const onSubmit = async (data: RosterFormValues) => {
    try {
      const result = roster
        ? await updateRoster({ id: roster.id, ...data }).unwrap()
        : await createRoster(data).unwrap();
      ModalInstance.close();
      onSaved?.(result);
    } catch (error) {
      if (isErrorWithDataAndStatus<RosterFormValues>(error)) {
        mapServerErrors(error.data).forEach((err) => {
          setError(err.name as keyof RosterFormValues, err.error);
        });
      }
    }
  };

  return (
    <Box>
      <Typography component="h2" variant="title" size="small" sx={{ mb: 3 }}>
        {roster ? "Edit roster" : "Create roster"}
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack sx={{ gap: 3 }}>
          <RhfTextField
            control={control}
            id="title"
            name="title"
            label="Title*"
            placeholder="e.g. Backend engineers Q3"
          />
          <RhfTextareaField
            control={control}
            id="description"
            name="description"
            label="Description"
            placeholder="What is this roster for?"
          />
          <Stack sx={{ gap: 1 }}>
            <Typography component="p" variant="label" id="visibility">
              Visibility
            </Typography>
            <Controller
              name="visibility"
              control={control}
              render={({ field: { value, onChange } }) => (
                <ButtonSelect
                  value={value}
                  onChange={(newValue) => {
                    if (newValue) {
                      onChange(newValue);
                    }
                  }}
                  options={VISIBILITY_OPTIONS}
                  variant="white-violet"
                  selectedVariant="violet"
                  shape="squared"
                  disableAll={!canChangeVisibility}
                  labelledBy="visibility"
                />
              )}
            />
            {!canChangeVisibility && (
              <Typography
                component="p"
                variant="paragraph"
                size="small"
                sx={{ color: "var(--grey-2)" }}
              >
                Only the owner can make this roster private again.
              </Typography>
            )}
          </Stack>
        </Stack>
        <Divider color="beige" sx={{ my: 3 }} />
        <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
          <Button
            variant="positive"
            shape="squared"
            size="medium"
            type="submit"
            disabled={formState.isSubmitting}
          >
            {roster ? "Save changes" : "Create roster"}
          </Button>
        </Stack>
      </form>
    </Box>
  );
};
